import React, { useState, useEffect } from 'react';
import { 
  MagnifyingGlassIcon, 
  FunnelIcon, 
  XMarkIcon,
  CalendarIcon,
  TagIcon,
  UserIcon
} from '@heroicons/react/24/outline';

const AdvancedSearch = ({ onSearch, onFilter, searchTerm, statusFilter, loading }) => {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [localSearchTerm, setLocalSearchTerm] = useState(searchTerm);
  const [localStatusFilter, setLocalStatusFilter] = useState(statusFilter);
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [dateRange, setDateRange] = useState('all');
  const [specificDate, setSpecificDate] = useState('');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [debounceTimer, setDebounceTimer] = useState(null);

  // Debounced search with immediate triggers for spaces and Enter
  useEffect(() => {
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }

    // Skip debounce if search term contains spaces (already handled in handleSearchChange)
    if (localSearchTerm.includes(' ')) {
      return;
    }

    const timer = setTimeout(() => {
      if (localSearchTerm !== searchTerm) {
        onSearch(localSearchTerm);
      }
    }, 200); // Reduced to 200ms for faster response

    setDebounceTimer(timer);

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [localSearchTerm, searchTerm, onSearch, debounceTimer]);

  // Update local state when props change
  useEffect(() => {
    setLocalSearchTerm(searchTerm);
  }, [searchTerm]);

  useEffect(() => {
    setLocalStatusFilter(statusFilter);
  }, [statusFilter]);

  const handleSearchChange = (value) => {
    setLocalSearchTerm(value);
    
    // Trigger immediate search on space or when adding spaces
    if (value.includes(' ') && value.trim().length > 0) {
      // Clear any existing debounce timer
      if (debounceTimer) {
        clearTimeout(debounceTimer);
        setDebounceTimer(null);
      }
      // Trigger search immediately
      onSearch(value);
    }
  };

  const handleStatusChange = (value) => {
    setLocalStatusFilter(value);
    onFilter({ status: value, priority: priorityFilter, dateRange, specificDate: dateRange === 'specific_date' ? specificDate : '', source: sourceFilter });
  };

  const handlePriorityChange = (value) => {
    setPriorityFilter(value);
    onFilter({ status: localStatusFilter, priority: value, dateRange, specificDate: dateRange === 'specific_date' ? specificDate : '', source: sourceFilter });
  };

  const handleDateRangeChange = (value) => {
    setDateRange(value);
    if (value !== 'specific_date') {
      setSpecificDate('');
    }
    onFilter({ status: localStatusFilter, priority: priorityFilter, dateRange: value, specificDate: value === 'specific_date' ? specificDate : '', source: sourceFilter });
  };

  const handleSpecificDateChange = (value) => {
    setSpecificDate(value);
    onFilter({ status: localStatusFilter, priority: priorityFilter, dateRange: 'specific_date', specificDate: value, source: sourceFilter });
  };

  const handleSourceChange = (value) => {
    setSourceFilter(value);
    onFilter({ status: localStatusFilter, priority: priorityFilter, dateRange, specificDate: dateRange === 'specific_date' ? specificDate : '', source: value });
  };

  const clearAllFilters = () => {
    setLocalSearchTerm('');
    setLocalStatusFilter('all');
    setPriorityFilter('all');
    setDateRange('all');
    setSpecificDate('');
    setSourceFilter('all');
    onSearch('');
    onFilter({ status: 'all', priority: 'all', dateRange: 'all', specificDate: '', source: 'all' });
  };

  const hasActiveFilters = localSearchTerm || localStatusFilter !== 'all' || priorityFilter !== 'all' || dateRange !== 'all' || (dateRange === 'specific_date' && specificDate) || sourceFilter !== 'all';

  return (
    <div className="space-y-4">
      {/* Main Search Bar */}
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
        </div>
        <input
          type="text"
          placeholder="Search leads by name, email, phone, company, or source..."
          value={localSearchTerm}
          onChange={(e) => handleSearchChange(e.target.value)}
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              // Clear any existing debounce timer
              if (debounceTimer) {
                clearTimeout(debounceTimer);
                setDebounceTimer(null);
              }
              // Trigger search immediately on Enter
              onSearch(localSearchTerm);
            }
          }}
          className="block w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
        />
        <div className="absolute inset-y-0 right-0 flex items-center pr-3 space-x-2">
          {hasActiveFilters && (
            <button
              onClick={clearAllFilters}
              className="text-gray-400 hover:text-gray-600"
              title="Clear all filters"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          )}
          <button
            onClick={() => {
              // Clear any existing debounce timer
              if (debounceTimer) {
                clearTimeout(debounceTimer);
                setDebounceTimer(null);
              }
              // Trigger search immediately
              onSearch(localSearchTerm);
            }}
            className="text-gray-400 hover:text-primary-600"
            title="Search now"
          >
            <MagnifyingGlassIcon className="h-4 w-4" />
          </button>
          <button
            onClick={() => setShowAdvanced(!showAdvanced)}
            className={`p-1 rounded-md ${
              showAdvanced ? 'bg-primary-100 text-primary-600' : 'text-gray-400 hover:text-gray-600'
            }`}
            title="Advanced filters"
          >
            <FunnelIcon className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Quick Date Filter Buttons */}
      <div className="flex flex-wrap gap-2">
        <span className="text-sm font-medium text-gray-700 mr-2">Quick Filters:</span>
        {[
          { value: 'today', label: 'Today' },
          { value: 'this_week', label: 'This Week' },
          { value: 'this_month', label: 'This Month' },
          { value: 'last_7_days', label: 'Last 7 Days' },
          { value: 'last_30_days', label: 'Last 30 Days' },
          { value: 'all', label: 'All Time' }
        ].map((filter) => (
          <button
            key={filter.value}
            onClick={() => handleDateRangeChange(filter.value)}
            className={`px-3 py-1 text-xs rounded-full border transition-colors ${
              dateRange === filter.value
                ? 'bg-primary-100 text-primary-700 border-primary-300'
                : 'bg-gray-50 text-gray-600 border-gray-300 hover:bg-gray-100'
            }`}
          >
            {filter.label}
          </button>
        ))}
      </div>

      {/* Advanced Filters */}
      {showAdvanced && (
        <div className="bg-gray-50 p-4 rounded-lg space-y-4">
          <div className="flex items-center space-x-2 mb-3">
            <FunnelIcon className="h-5 w-5 text-gray-600" />
            <h3 className="text-sm font-medium text-gray-900">Advanced Filters</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Status Filter */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                <UserIcon className="h-3 w-3 inline mr-1" />
                Status
              </label>
              <select
                value={localStatusFilter}
                onChange={(e) => handleStatusChange(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="all">All Status</option>
                <option value="new">New</option>
                <option value="in_progress">In Progress</option>
                <option value="follow_up">Follow Up</option>
                <option value="qualified">Qualified</option>
                <option value="converted">Converted</option>
                <option value="closed">Closed</option>
                <option value="lost">Lost</option>
              </select>
            </div>

            {/* Priority Filter */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                <TagIcon className="h-3 w-3 inline mr-1" />
                Priority
              </label>
              <select
                value={priorityFilter}
                onChange={(e) => handlePriorityChange(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="all">All Priority</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>

            {/* Date Range Filter */}
            <div className={dateRange === 'specific_date' ? 'md:col-span-2 lg:col-span-1' : ''}>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                <CalendarIcon className="h-3 w-3 inline mr-1" />
                Date Range
              </label>
              <select
                value={dateRange}
                onChange={(e) => handleDateRangeChange(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="all">All Time</option>
                <option value="today">Today</option>
                <option value="yesterday">Yesterday</option>
                <option value="this_week">This Week</option>
                <option value="last_week">Last Week</option>
                <option value="this_month">This Month</option>
                <option value="last_month">Last Month</option>
                <option value="last_7_days">Last 7 Days</option>
                <option value="last_30_days">Last 30 Days</option>
                <option value="last_90_days">Last 90 Days</option>
                <option value="this_quarter">This Quarter</option>
                <option value="last_quarter">Last Quarter</option>
                <option value="this_year">This Year</option>
                <option value="last_year">Last Year</option>
                <option value="specific_date">Specific Date</option>
              </select>
            </div>

            {/* Specific Date Input - Show only when "Specific Date" is selected */}
            {dateRange === 'specific_date' && (
              <div className="md:col-span-2 lg:col-span-1">
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  <CalendarIcon className="h-3 w-3 inline mr-1" />
                  Select Date
                </label>
                <input
                  type="date"
                  value={specificDate}
                  onChange={(e) => handleSpecificDateChange(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            )}

            {/* Source Filter */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                <MagnifyingGlassIcon className="h-3 w-3 inline mr-1" />
                Source
              </label>
              <select
                value={sourceFilter}
                onChange={(e) => handleSourceChange(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="all">All Sources</option>
                <option value="Website">Website</option>
                <option value="Google Search">Google Search</option>
                <option value="Social Media">Social Media</option>
                <option value="WhatsApp Lead">WhatsApp Lead</option>
                <option value="Referral">Referral</option>
                <option value="Advertisement">Advertisement</option>
                <option value="Cold Call">Cold Call</option>
                <option value="Other">Other</option>
              </select>
            </div>
          </div>

          {/* Active Filters Summary */}
          {hasActiveFilters && (
            <div className="pt-3 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div className="text-xs text-gray-600">
                  Active filters: {[
                    localSearchTerm && 'Search',
                    localStatusFilter !== 'all' && 'Status',
                    priorityFilter !== 'all' && 'Priority',
                    dateRange !== 'all' && (dateRange === 'specific_date' && specificDate ? `Date: ${specificDate}` : 'Date'),
                    sourceFilter !== 'all' && 'Source'
                  ].filter(Boolean).join(', ')}
                </div>
                <button
                  onClick={clearAllFilters}
                  className="text-xs text-primary-600 hover:text-primary-800 font-medium"
                >
                  Clear All
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Loading Indicator */}
      {loading && (
        <div className="flex items-center justify-center py-2">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
          <span className="ml-2 text-sm text-gray-600">Searching...</span>
        </div>
      )}
    </div>
  );
};

export default AdvancedSearch;
